import React, { ReactElement, useContext, useEffect, useRef, useState } from 'react'
import Highcharts from 'highcharts2/highstock'
import styled, { useTheme } from 'styled-components'
import i18n from 'live/services/i18n'
import { RawRunEvent } from '../../utils/type'
import { ChartContainer, ChartTitle } from '../chart-components-style'
import { getRunChartOptions } from './chart-options'
import {
    renderButtons,
    StaticDataTypeI18nKey,
    ButtonOptions,
    STATIC_DATA_STATES,
    renderCreationTooltip,
    DeleteConfirmationModal
} from '../chart-components'
import { StaticDataModal } from '../modal-components'
import { deleteSingleEntity, RUN_API } from '../../api/api'
import { StaticDataContext } from '../../context/StaticDataContext'
import { RunModal } from './RunModal'

require('highcharts/modules/xrange')(Highcharts)

export const RunChart = (): ReactElement => {
    // Static Data Context
    const {
        buttonsRef,
        buttonController,
        setButtonController,
        currentChart,
        setAddEventButtonVisible,
        chartType,
        tooltipPosition,
        setTooltiPosition,
        widgetData,
        widgetTimeSpan,
        refresh,
        tryOpenModal,
        reloadData,
        checkBlockedTrackBeforeAction
    } = useContext(StaticDataContext)

    const [isSharedComponent, setIsSharedComponent] = useState<boolean>(false)
    const [tubulars, setTubulars] = useState<any[]>([])
    const [isModalDoubleSize, setIsModalDoubleSize] = useState<boolean>(false)

    // Chart States
    const [highchartsElement, setHighchartsElement] = useState<Highcharts.Chart | null>(null)
    const [chartLoaded, setChartLoaded] = useState<boolean>(false)
    const [chartHeight, setChartHeight] = useState<number>(0)
    const chartRef = useRef<HTMLDivElement>(null)
    const theme = useTheme()

    // Modal States
    const [currentModalEvent, setCurrentModalEvent] = useState<RawRunEvent>(null)
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
    const closeModal = (): void => {
        setIsModalOpen(false)
        setIsModalDoubleSize(false)
    }
    const openModal = (rawEvent: RawRunEvent): void => {
        setCurrentModalEvent(rawEvent)
        setRunTooltipMessage(null)
        setIsModalOpen(true)
    }

    // Delete Confirmation Modal State
    const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] = useState<boolean>(false)
    const closeDeleteConfirmationModal = (): void => setIsDeleteConfirmationModalOpen(false)
    const openDeleteConfirmationModal = (rawEvent: RawRunEvent): void => {
        setCurrentModalEvent(rawEvent)
        setIsDeleteConfirmationModalOpen(true)
    }

    const deleteRun = (runId: any) => {
        return deleteSingleEntity(RUN_API, runId).then(() => {
            closeDeleteConfirmationModal()
            reloadData()
        })
    }

    function draw(): void {
        try {
            highchartsElement?.destroy()
            setChartLoaded(false)
            setHighchartsElement(
                Highcharts.chart(
                    chartRef?.current,
                    getRunChartOptions(
                        { timeSpan: widgetTimeSpan, height: chartHeight },
                        widgetData.runSeries,
                        theme,
                        openModal,
                        tryOpenModal,
                        setAddEventButtonVisible,
                        setTooltiPosition,
                        setRunTooltipMessage,
                        checkBlockedTrackBeforeAction
                    ),
                    () => setChartLoaded(true)
                )
            )
        } catch (error) {
            // highchartsElement does not exits.
        }
    }

    useEffect(() => setChartHeight(chartRef.current.getBoundingClientRect().height), [])

    useEffect(() => draw(), [widgetData.runSeries])

    useEffect(() => {
        if (chartLoaded) {
            setTimeout(() => {
                draw()
            }, 300)
        }
    }, [refresh, chartHeight, theme])

    const buttonProps = {
        buttonFunctions: [
            () =>
                checkBlockedTrackBeforeAction(currentChart, () => {
                    openDeleteConfirmationModal(currentChart?.rawEvent as RawRunEvent)
                    setButtonController(false)
                }),
            () =>
                checkBlockedTrackBeforeAction(currentChart, () => {
                    openModal(currentChart?.rawEvent as RawRunEvent)
                    setButtonController(false)
                })
        ],
        customIcons: [ButtonOptions.Delete, ButtonOptions.Confirm]
    }

    const [runTooltipMessage, setRunTooltipMessage] = useState(null)

    return (
        <>
            <ChartContainer>
                <StaticDataModal
                    isOpen={isModalOpen}
                    isDouble={isSharedComponent || isModalDoubleSize}
                    closeModal={closeModal}
                    content={
                        <RunModal
                            event={currentModalEvent}
                            closeModal={closeModal}
                            isSharedComponent={isSharedComponent}
                            setIsSharedComponent={setIsSharedComponent}
                            tubulars={tubulars}
                            setTubulars={setTubulars}
                            isModalDoubleSize={isModalDoubleSize}
                            setIsModalDoubleSize={setIsModalDoubleSize}
                        />
                    }
                />
                <ChartTitle>
                    <span>{i18n(StaticDataTypeI18nKey.RUN).toUpperCase()}</span>
                </ChartTitle>
                <Chart ref={chartRef} />
                {buttonController && buttonController === STATIC_DATA_STATES.EMPTY_DISABLED ? (
                    <div></div>
                ) : buttonController && buttonController !== STATIC_DATA_STATES.EMPTY ? (
                    chartType === 'run' && renderButtons(buttonsRef, currentChart, buttonProps)
                ) : (
                    chartType === 'run' &&
                    renderCreationTooltip(
                        buttonsRef,
                        currentChart,
                        tooltipPosition,
                        buttonController,
                        runTooltipMessage
                    )
                )}
            </ChartContainer>
            <DeleteConfirmationModal
                isOpen={isDeleteConfirmationModalOpen}
                closeModal={closeDeleteConfirmationModal}
                confirmDeletion={() =>
                    deleteRun((currentChart?.rawEvent as RawRunEvent).id).then(() => {
                        currentChart.remove()
                        setButtonController(false)
                    })
                }
            />
        </>
    )
}

const Chart = styled.div`
    height: 100%;
    width: 100%;
    max-height: 30px;
    min-height: 20px;
    margin-top: -16px;

    .highcharts-container {
        max-height: 30px;
        min-height: 20px;
        .highcharts-root {
            max-height: 30px;
            min-height: 20px;
            .highcharts-series-group {
                max-height: 30px;
                min-height: 20px;
                .highcharts-series highcharts-series-0 highcharts-xrange-series highcharts-tracker {
                    max-height: 30px;
                    min-height: 20px;
                    .highcharts-point {
                        max-height: 30px;
                        min-height: 20px;
                        .highcharts-point highcharts-partfill-original {
                            max-height: 30px;
                            min-height: 20px;
                        }
                    }
                }
            }
        }
    }
`
